// Location Risk Hooks (Phase 4)
// React hooks for accessing and managing location risk profiles

import { useState, useEffect } from 'react';
import { locationRiskService } from '../services/locationRiskService';

/**
 * Hook to get location risk profile
 */
export function useLocationRiskProfile(locationId, locationType = 'room', options = {}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!locationId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if we should recalculate
        if (options.recalculate) {
          const calculated = await locationRiskService.calculateLocationRisk(locationId, locationType);
          setProfile(calculated);
        } else {
          // Get existing profile
          const existing = await locationRiskService.getLocationRiskProfile(locationId, locationType);
          
          if (existing) {
            // Check if it's outdated (older than calculation frequency)
            const lastCalculated = existing.last_calculated?.toDate?.() || new Date(0);
            const hoursSinceCalculation = (Date.now() - lastCalculated.getTime()) / (1000 * 60 * 60);
            
            if (hoursSinceCalculation > 1 || options.forceRefresh) {
              // Recalculate if outdated or forced
              const calculated = await locationRiskService.calculateLocationRisk(locationId, locationType);
              setProfile(calculated);
            } else {
              setProfile(existing);
            }
          } else {
            // No profile exists, calculate it
            const calculated = await locationRiskService.calculateLocationRisk(locationId, locationType);
            setProfile(calculated);
          }
        }
      } catch (err) {
        console.error('Error loading location risk profile:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [locationId, locationType, options.recalculate, options.forceRefresh]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const calculated = await locationRiskService.calculateLocationRisk(locationId, locationType);
      setProfile(calculated);
    } catch (err) {
      console.error('Error refreshing location risk profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { profile, loading, error, refresh };
}

/**
 * Hook to get location risk multiplier (quick access)
 */
export function useLocationRiskMultiplier(locationId, locationType = 'room') {
  const [multiplier, setMultiplier] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!locationId) {
      setMultiplier(1.0);
      setLoading(false);
      return;
    }

    const loadMultiplier = async () => {
      try {
        setLoading(true);
        setError(null);
        const mult = await locationRiskService.getLocationMultiplier(locationId, locationType);
        setMultiplier(mult);
      } catch (err) {
        console.error('Error loading location risk multiplier:', err);
        setError(err.message);
        setMultiplier(1.0); // Default to 1.0 on error
      } finally {
        setLoading(false);
      }
    };

    loadMultiplier();
  }, [locationId, locationType]);

  return { multiplier, loading, error };
}

/**
 * Hook to get all location risk profiles
 */
export function useAllLocationRiskProfiles(locationType, parentId = null) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        setLoading(true);
        setError(null);
        const allProfiles = await locationRiskService.getAllLocationRiskProfiles(locationType, parentId);
        console.log(`Loaded ${allProfiles.length} profiles for ${locationType}:`, allProfiles);
        setProfiles(allProfiles);
      } catch (err) {
        console.error('Error loading location risk profiles:', err);
        // Don't set error if it's just index building - profiles might still exist
        if (!err.message || !err.message.includes('index')) {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfiles();
  }, [locationType, parentId]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const allProfiles = await locationRiskService.getAllLocationRiskProfiles(locationType, parentId);
      setProfiles(allProfiles);
    } catch (err) {
      console.error('Error refreshing location risk profiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = (newProfile) => {
    if (!newProfile || !newProfile.location_id || !newProfile.location_type) {
      console.warn('Invalid profile provided to updateProfile:', newProfile);
      return;
    }
    
    console.log('updateProfile called with:', newProfile);
    setProfiles(prev => {
      const existingIndex = prev.findIndex(p => 
        p.location_id === newProfile.location_id && 
        p.location_type === newProfile.location_type
      );
      if (existingIndex >= 0) {
        // Update existing profile
        const updated = [...prev];
        updated[existingIndex] = newProfile;
        console.log(`Updated profile at index ${existingIndex}, new profiles count: ${updated.length}`);
        return updated;
      } else {
        // Add new profile
        const added = [...prev, newProfile];
        console.log(`Added new profile, new profiles count: ${added.length}`);
        return added;
      }
    });
  };

  return { profiles, loading, error, refresh, updateProfile };
}
